import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CalendarCheck, MapPin, TShirt, Backpack, VideoCamera, Clock, CalendarPlus, DownloadSimple, UsersThree, Printer } from "@phosphor-icons/react";
import { deviceId } from "@/lib/installPrompt";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Row = ({ icon: Icon, title, children, testid }) => (
  <div className="rounded-xl border border-white/10 p-4 flex gap-3" data-testid={testid}>
    <Icon size={20} color="#FFE01A" weight="duotone" className="shrink-0 mt-0.5" />
    <div>
      <div className="font-bold font-display text-white text-sm">{title}</div>
      <div className="text-[#D4D4D8] text-sm leading-relaxed mt-0.5">{children}</div>
    </div>
  </div>
);

export default function SessionInfoPage() {
  const { id } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState(false);
  const [count, setCount] = useState(0);
  const [attendees, setAttendees] = useState([]);
  const [myName, setMyName] = useState(() => localStorage.getItem("saproject:rsvp-name") || "");
  const [myPhone, setMyPhone] = useState(() => localStorage.getItem("saproject:rsvp-phone") || "");
  const [going, setGoing] = useState(() => localStorage.getItem(`saproject:rsvp:${window.location.pathname}`) === "1");
  const [waitlisted, setWaitlisted] = useState(() => localStorage.getItem(`saproject:waitlist:${window.location.pathname}`) === "1");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.title = "Session details — The Self-Agency Project";
    fetch(`${API}/site/session/${id}`)
      .then((r) => { if (!r.ok) throw new Error(); return r.json(); })
      .then((d) => { setInfo(d); setCount(d.rsvp_count || 0); setAttendees(d.attendees || []); }).catch(() => setErr(true));
  }, [id]);

  const full = info?.spots_left === 0 && !going;
  const rsvp = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/site/session/${id}/rsvp`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ device_id: deviceId(), going: !going, name: myName.trim().split(" ")[0], phone: myPhone.trim() }),
      });
      if (!r.ok) throw new Error();
      const d = await r.json();
      setCount(d.count);
      setAttendees(d.attendees || []);
      setGoing(!going);
      setInfo((prev) => prev && prev.capacity ? { ...prev, spots_left: Math.max(0, prev.capacity - d.count) } : prev);
      localStorage.setItem(`saproject:rsvp:${window.location.pathname}`, !going ? "1" : "0");
      if (myName.trim()) localStorage.setItem("saproject:rsvp-name", myName.trim());
      if (myPhone.trim()) localStorage.setItem("saproject:rsvp-phone", myPhone.trim());
    } catch { /* ignore */ }
    finally { setBusy(false); }
  };

  const waitlist = async () => {
    if (!waitlisted && !myPhone.trim()) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/site/session/${id}/waitlist`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ device_id: deviceId(), going: !waitlisted, name: myName.trim().split(" ")[0], phone: myPhone.trim() }),
      });
      if (!r.ok) throw new Error();
      setWaitlisted(!waitlisted);
      localStorage.setItem(`saproject:waitlist:${window.location.pathname}`, !waitlisted ? "1" : "0");
      if (myName.trim()) localStorage.setItem("saproject:rsvp-name", myName.trim());
      if (myPhone.trim()) localStorage.setItem("saproject:rsvp-phone", myPhone.trim());
    } catch { /* ignore */ }
    finally { setBusy(false); }
  };

  if (err) return (
    <div className="min-h-screen bg-black text-white grid place-items-center px-5">
      <p className="text-[#A1A1AA]" data-testid="session-not-found">Session not found — double-check the link with whoever shared it.</p>
    </div>
  );
  if (!info) return <div className="min-h-screen bg-black" />;

  const cancelled = !["pending", "confirmed"].includes(info.status);
  const virtual = info.session_type === "virtual";
  const gcalUrl = info.start_utc && `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(info.title || "Self-Agency session")}&dates=${info.start_utc}/${info.end_utc}&location=${encodeURIComponent(virtual ? (info.meet_link || "Online") : (info.location || ""))}&details=${encodeURIComponent(`Details: ${window.location.href}`)}`;
  let nice = info.date;
  try {
    nice = new Date(`${info.date}T${info.time}:00`).toLocaleDateString("en-CA", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
  } catch { /* keep iso */ }

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-xl mx-auto px-5 py-14" data-testid="session-info-page">
        <div className="text-[#FFE01A] text-xs font-bold uppercase tracking-wide">The Self-Agency Project · Elevation650</div>
        <h1 className="text-4xl sm:text-5xl font-extrabold font-display mt-2 mb-1" style={{ color: "#fff" }}>
          {virtual ? "Your virtual session" : "Your session"}
        </h1>
        {cancelled ? (
          <div className="mt-4 rounded-xl border border-red-400/50 bg-red-500/10 px-4 py-3 text-sm" data-testid="session-cancelled">
            This session was cancelled or rescheduled — check with {info.booked_by || "your organizer"} for the latest details.
          </div>
        ) : (
          <>
            <p className="text-[#A1A1AA] text-sm mb-6">Booked by {info.booked_by || "your organizer"} — everything you need below.</p>
            {info.image_url && (
              <img src={info.image_url} alt="The Self-Agency Project" data-testid="session-image"
                className="w-full h-44 object-cover rounded-xl border border-white/10 mb-4" />
            )}
            <div className="space-y-3">
              <Row icon={CalendarCheck} title="When" testid="session-when">
                <b className="text-white">{nice}</b> at <b className="text-white">{info.time12}</b>
                <span className="text-[#71717A]"> ({info.timezone.replace("America/", "")} time)</span>
              </Row>
              <Row icon={Clock} title="How long" testid="session-duration">About {info.duration_minutes} minutes — arrive 10 minutes early.</Row>
              {virtual ? (
                <Row icon={VideoCamera} title="Where" testid="session-where">
                  Online{info.meet_link ? <> — join here: <a href={info.meet_link} className="text-[#FFE01A]">{info.meet_link}</a></> : " — your organizer has the meeting link."}
                </Row>
              ) : (
                <Row icon={MapPin} title="Where" testid="session-where">
                  {info.location || "Location shared by your organizer."}
                  {info.maps_url && <> · <a href={info.maps_url} target="_blank" rel="noreferrer" className="text-[#FFE01A]">Open map</a></>}
                </Row>
              )}
              {info.what_to_wear && <Row icon={TShirt} title="What to wear" testid="session-wear">{info.what_to_wear}</Row>}
              {info.bring && <Row icon={Backpack} title="What to bring" testid="session-bring">{info.bring}</Row>}
            </div>
            {gcalUrl && (
              <div className="flex flex-wrap gap-2 mt-5" data-testid="session-calendar-buttons">
                <a href={gcalUrl} target="_blank" rel="noreferrer" data-testid="session-gcal-btn"
                  className="inline-flex items-center gap-2 bg-[#FFE01A] text-black font-bold text-sm px-4 py-2 rounded-full no-underline hover:opacity-90">
                  <CalendarPlus size={16} weight="bold" /> Add to Google Calendar
                </a>
                <a href={`${API}/site/session/${id}/calendar.ics`} data-testid="session-ics-btn"
                  className="inline-flex items-center gap-2 border border-white/25 text-white font-bold text-sm px-4 py-2 rounded-full no-underline hover:border-[#FFE01A]">
                  <DownloadSimple size={16} weight="bold" /> Apple / Outlook (.ics)
                </a>
              </div>
            )}
            <div className="mt-4 rounded-xl border border-[#FFE01A]/40 p-4" data-testid="session-rsvp">
              <div className="flex flex-wrap items-center gap-3">
                <UsersThree size={22} color="#FFE01A" weight="duotone" />
                <div className="text-sm text-[#D4D4D8] flex-1 min-w-[140px]" data-testid="session-rsvp-count">
                  <b className="text-white">{count}</b> {count === 1 ? "person is" : "people are"} coming
                  {info.capacity != null && (
                    <span className="ml-2 text-xs font-bold" data-testid="session-spots-left"
                      style={{ color: info.spots_left === 0 ? "#F87171" : "#FFE01A" }}>
                      {info.spots_left === 0 ? "Session full" : `${info.spots_left} spot${info.spots_left === 1 ? "" : "s"} left`}
                    </span>
                  )}
                </div>
                {!going && !full && (
                  <>
                    <input value={myName} onChange={(e) => setMyName(e.target.value)} maxLength={60}
                      placeholder="First name (optional)" data-testid="session-rsvp-name"
                      className="rounded-full bg-white/5 border border-white/15 text-white text-sm px-4 py-2 placeholder:text-[#71717A] focus:outline-none focus:border-[#FFE01A] w-44" />
                    <input value={myPhone} onChange={(e) => setMyPhone(e.target.value)} maxLength={24} type="tel"
                      placeholder="Mobile — reminder text (optional)" data-testid="session-rsvp-phone"
                      className="rounded-full bg-white/5 border border-white/15 text-white text-sm px-4 py-2 placeholder:text-[#71717A] focus:outline-none focus:border-[#FFE01A] w-60" />
                  </>
                )}
                <button onClick={rsvp} disabled={busy || full} data-testid="session-rsvp-btn"
                  className={"font-bold text-sm px-5 py-2 rounded-full border cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed " +
                    (going ? "bg-transparent text-[#FFE01A] border-[#FFE01A]" : "bg-[#FFE01A] text-black border-[#FFE01A]")}>
                  {going ? "You're in ✓ — tap to undo" : full ? "Session full" : "I'm coming"}
                </button>
              </div>
              {!going && !full && myPhone.trim() !== "" && (
                <div className="text-[11px] text-[#71717A] mt-2">We'll text you one reminder the morning of the session — nothing else.</div>
              )}
              {full && (
                <div className="mt-3 pt-3 border-t border-white/10" data-testid="session-waitlist">
                  {waitlisted ? (
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="text-sm text-[#D4D4D8] flex-1 min-w-[180px]" data-testid="session-waitlist-status">
                        <b className="text-[#FFE01A]">You're on the waitlist ✓</b> — we'll text you the moment a spot opens. First come, first served.
                      </div>
                      <button onClick={waitlist} disabled={busy} data-testid="session-waitlist-leave-btn"
                        className="font-bold text-xs px-4 py-2 rounded-full border border-white/25 bg-transparent text-[#A1A1AA] cursor-pointer hover:border-[#FFE01A] disabled:opacity-50">
                        Leave waitlist
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="text-sm text-[#D4D4D8] w-full">Session full — join the waitlist and we'll <b className="text-white">text you if a spot opens</b>.</div>
                      <input value={myName} onChange={(e) => setMyName(e.target.value)} maxLength={60}
                        placeholder="First name (optional)" data-testid="session-waitlist-name"
                        className="rounded-full bg-white/5 border border-white/15 text-white text-sm px-4 py-2 placeholder:text-[#71717A] focus:outline-none focus:border-[#FFE01A] w-44" />
                      <input value={myPhone} onChange={(e) => setMyPhone(e.target.value)} maxLength={24} type="tel"
                        placeholder="Mobile number *" data-testid="session-waitlist-phone"
                        className="rounded-full bg-white/5 border border-white/15 text-white text-sm px-4 py-2 placeholder:text-[#71717A] focus:outline-none focus:border-[#FFE01A] w-48" />
                      <button onClick={waitlist} disabled={busy || !myPhone.trim()} data-testid="session-waitlist-btn"
                        className="font-bold text-sm px-5 py-2 rounded-full border border-[#FFE01A] bg-transparent text-[#FFE01A] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
                        Waitlist me
                      </button>
                    </div>
                  )}
                </div>
              )}
              {attendees.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3" data-testid="session-attendees">
                  {attendees.map((n, i) => (
                    <span key={i} className="text-xs font-semibold px-2.5 py-1 rounded-full border border-white/15 text-[#D4D4D8]">{n}</span>
                  ))}
                  {count > attendees.length && (
                    <span className="text-xs px-2.5 py-1 text-[#71717A]">+{count - attendees.length} more</span>
                  )}
                </div>
              )}
            </div>
            <Link to={`/session/${id}/poster`} data-testid="session-poster-link"
              className="inline-flex items-center gap-1.5 mt-4 text-xs font-bold text-[#A1A1AA] no-underline hover:text-[#FFE01A]">
              <Printer size={14} /> Hosting? Print a door poster with a scannable code →
            </Link>
          </>
        )}
        <p className="text-[#71717A] text-xs mt-8">Questions? Call or text +1 780-228-5325 · admin@elevation650.com</p>
      </div>
    </div>
  );
}
